/**
 * Teacher's profile — read email, edit name/bio/photo_url, change password.
 */
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../api";
import { useTeacherAuth } from "../TeacherAuthContext";

export default function TeacherProfile() {
  const { status, teacher, token, setTeacher, signingOutRef } = useTeacherAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [bio, setBio] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState({ kind: "", text: "" });

  const [currPw, setCurrPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [pwMsg, setPwMsg] = useState({ kind: "", text: "" });

  useEffect(() => {
    if (status === "anon" && !signingOutRef.current) navigate("/teachers", { replace: true });
  }, [status, navigate, signingOutRef]);

  useEffect(() => {
    if (teacher) {
      setName(teacher.name || "");
      setBio(teacher.bio || "");
      setPhotoUrl(teacher.photo_url || "");
    }
  }, [teacher]);

  if (status !== "auth") return null;

  async function saveProfile(e) {
    e.preventDefault();
    setSavingProfile(true);
    setProfileMsg({ kind: "", text: "" });
    try {
      const updated = await api.teacherUpdateMe(token, {
        name: name || null,
        bio: bio || null,
        photo_url: photoUrl || null,
      });
      setTeacher(updated);
      setProfileMsg({ kind: "ok", text: "Profile updated." });
    } catch (err) {
      setProfileMsg({ kind: "err", text: err.message || "Could not save." });
    } finally {
      setSavingProfile(false);
    }
  }

  async function changePassword(e) {
    e.preventDefault();
    setSavingPw(true);
    setPwMsg({ kind: "", text: "" });
    try {
      await api.teacherChangePassword(token, currPw, newPw);
      setCurrPw("");
      setNewPw("");
      setTeacher({ ...teacher, must_change_password: false });
      setPwMsg({ kind: "ok", text: "Password changed." });
    } catch (err) {
      setPwMsg({ kind: "err", text: err.message || "Could not change password." });
    } finally {
      setSavingPw(false);
    }
  }

  return (
    <main className="min-h-[100dvh] bg-[#FAF9F6] pt-20 md:pt-24 pb-24 px-6 md:px-10" data-testid="teacher-profile-page">
      <div className="max-w-2xl mx-auto">
        <Link to="/teachers/dashboard" className="inline-block text-[10px] tracking-[0.28em] uppercase text-[#1C1C1C]/55 hover:text-[#1C1C1C] mb-6 transition-colors">
          ← Dashboard
        </Link>
        <h1 className="font-display text-4xl md:text-6xl leading-tight tracking-tighter font-light text-[#1C1C1C]">
          My profile.
        </h1>

        <section className="mt-10 bg-white border border-[#1C1C1C]/10 rounded-2xl p-6 md:p-8">
          <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
            Account
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mb-6">
            <ReadOnly label="Email (sign-in)" value={teacher.email} />
            <ReadOnly label="Account created" value={teacher.created_at ? new Date(teacher.created_at).toLocaleDateString() : "—"} />
          </div>

          <form onSubmit={saveProfile} className="space-y-4" data-testid="teacher-profile-form">
            <Field label="Name">
              <input data-testid="teacher-profile-name" required value={name} onChange={(e) => setName(e.target.value)} className="input" />
            </Field>
            <Field label="Bio">
              <textarea data-testid="teacher-profile-bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={4} className="input resize-y" />
            </Field>
            <Field label="Photo URL (optional)">
              <input data-testid="teacher-profile-photo-url" type="url" value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} className="input" placeholder="https://…" />
            </Field>
            {profileMsg.text && (
              <div data-testid="teacher-profile-msg" className={`text-sm ${profileMsg.kind === "ok" ? "text-emerald-700" : "text-[#B83A3A]"}`}>
                {profileMsg.text}
              </div>
            )}
            <button
              data-testid="teacher-profile-submit"
              type="submit"
              disabled={savingProfile}
              className="px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors"
            >
              {savingProfile ? "Saving…" : "Save"}
            </button>
          </form>
        </section>

        <section className="mt-8 bg-white border border-[#1C1C1C]/10 rounded-2xl p-6 md:p-8">
          <div className="text-[10px] tracking-[0.32em] uppercase font-medium text-[#1C1C1C]/60 mb-4">
            Password
          </div>
          {teacher.must_change_password && (
            <div className="mb-4 text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
              You're using a temporary password. Please change it now.
            </div>
          )}
          <form onSubmit={changePassword} className="space-y-4" data-testid="teacher-password-form">
            <Field label="Current password">
              <input data-testid="teacher-current-password" type="password" required value={currPw} onChange={(e) => setCurrPw(e.target.value)} className="input" autoComplete="current-password" />
            </Field>
            <Field label="New password (min 8 characters)">
              <input data-testid="teacher-new-password" type="password" required minLength={8} value={newPw} onChange={(e) => setNewPw(e.target.value)} className="input" autoComplete="new-password" />
            </Field>
            {pwMsg.text && (
              <div data-testid="teacher-password-msg" className={`text-sm ${pwMsg.kind === "ok" ? "text-emerald-700" : "text-[#B83A3A]"}`}>
                {pwMsg.text}
              </div>
            )}
            <button
              data-testid="teacher-password-submit"
              type="submit"
              disabled={savingPw}
              className="px-5 py-3 bg-[#1C1C1C] text-[#FAF9F6] rounded-xl text-sm tracking-[0.16em] uppercase font-medium hover:bg-[#1C1C1C]/85 disabled:opacity-40 transition-colors"
            >
              {savingPw ? "Updating…" : "Change password"}
            </button>
          </form>
        </section>
        <style>{`.input{width:100%;padding:.7rem 1rem;background:#FAF9F6;border:1px solid rgba(28,28,28,0.1);border-radius:.75rem;font-size:0.95rem;outline:none}.input:focus{border-color:rgba(28,28,28,0.4);background:white}`}</style>
      </div>
    </main>
  );
}

function ReadOnly({ label, value }) {
  return (
    <div>
      <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/55 mb-1">{label}</div>
      <div className="font-mono text-sm text-[#1C1C1C]">{value}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] tracking-[0.28em] uppercase font-medium text-[#1C1C1C]/55 mb-2">{label}</div>
      {children}
    </label>
  );
}
